import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useI18n } from '../i18n/index.jsx'
import ReactGA from 'react-ga4'
import { createClient } from '@supabase/supabase-js'
import { GlobalCookieConsent } from '../components/GlobalCookieConsent'
import { logReferralEvent } from '../components/ReferralManager'

// Initialize Supabase with environment variables
// ✅ Using VITE_* variables (safe for frontend)
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseKey) {
  console.error('❌ Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY in environment variables')
}

const supabase = createClient(supabaseUrl || '', supabaseKey || '')

export default function Signup() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({ email: '', pseudo: '', password: '' })
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState(0)
  const [passwordErrors, setPasswordErrors] = useState([])

  // Validation du mot de passe
  const validatePassword = (pwd) => {
    const errors = []
    let strength = 0

    // Vérifier la longueur
    if (pwd.length < 8) {
      errors.push('min8')
    } else {
      strength += 25
    }

    // Vérifier les caractères spéciaux
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)) {
      errors.push('special')
    } else {
      strength += 25
    }

    // Vérifier les majuscules
    if (!/[A-Z]/.test(pwd)) {
      errors.push('uppercase')
    } else {
      strength += 25
    }

    // Vérifier les chiffres
    if (!/[0-9]/.test(pwd)) {
      errors.push('number')
    } else {
      strength += 25
    }

    return { strength: Math.min(100, strength), errors }
  }

  // Track page view
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: "/signup", title: "Signup" });
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    
    // Valider le mot de passe en temps réel
    if (name === 'password') {
      const { strength, errors } = validatePassword(value)
      setPasswordStrength(strength)
      setPasswordErrors(errors)
    }
  }
  
  const onSubmit = async (e) => {
    e.preventDefault()
    console.log('📝 Formulaire soumis - Début du signup...');

    setLoading(true)
    setError('')
    setSuccess(false)

    // Log la valeur du code referral dans le localStorage au moment du submit
    const refCodeAtSubmit = localStorage.getItem('referrer_code');
    console.log('[DEBUG] referrer_code dans localStorage au submit:', refCodeAtSubmit);

    // Vérifier que le mot de passe est valide
    const { strength, errors } = validatePassword(formData.password)
    if (errors.length > 0 || strength < 50) {
      setError('Le mot de passe doit contenir au moins 8 caractères avec un caractère spécial')
      setLoading(false)
      return
    }

    try {
  const lang = navigator.language ? navigator.language.split('-')[0] : 'fr';
  console.log('🌐 Envoi de la requête signup à l\'API...');
  console.log('  - Email:', formData.email);
  console.log('  - Pseudo:', formData.pseudo);
  console.log('  - Lang:', lang);
  
  const response = await fetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pseudo: formData.pseudo,
          email: formData.email,
          password: formData.password,
          lang: lang
        })
      })

      const data = await response.json()

      console.log('📋 Réponse du serveur signup:');
      console.log('  - Status:', response.status);
      console.log('  - OK:', response.ok);
      console.log('  - Data:', data);

      if (response.ok) {
        setSuccess(true)

        // Stocker le token JWT reçu du signup
        const jwtToken = data.access_token || data.token;
        console.log('[DEBUG] JWT Token reçu du signup:', jwtToken ? '✅ Présent' : '❌ Absent');
        
        if (jwtToken) {
          // Appeler /auth/profile pour récupérer l'id du user
          try {
            console.log('[DEBUG] Appel à /auth/profile pour récupérer l\'id du user...');
            const profileResp = await fetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/auth/profile', {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${jwtToken}`,
                'Content-Type': 'application/json'
              }
            });
            const profileData = await profileResp.json();
            console.log('[DEBUG] Réponse /auth/profile:', profileData);
            
            if (profileResp.ok && profileData.id) {
              const userId = profileData.id;
              if (!userId) {
                console.error('[AFFILIATION] ❌ Aucun id trouvé dans la réponse de /auth/profile:', profileData);
              } else {
                console.log('[DEBUG] User ID récupéré:', userId);
                // Tenter l'affiliation avec l'id du user
                const refCode = localStorage.getItem('referrer_code');
                console.log('[DEBUG] Avant appel affiliation:', { user_id: userId, refCode });
                if (refCode) {
                  try {
                    const affiliationResult = await logReferralEvent({
                      event: 'signup',
                      userId: userId,
                      price_id: null,
                      timestamp: Date.now()
                    });
                    console.log('✅ Affiliation trackée:', affiliationResult);
                  } catch (affiliationError) {
                    console.warn('⚠️ Erreur affiliation (non-bloquant):', affiliationError);
                  }
                } else {
                  console.warn('❌ Aucun code referral trouvé dans localStorage au moment du signup!');
                }
                // Stocker les infos de session
                localStorage.setItem('access_token', jwtToken);
                localStorage.setItem('token_type', 'Bearer');
                localStorage.setItem('email', formData.email);
                localStorage.setItem('user_id', userId);
              }
              // Rediriger
              setTimeout(() => navigate('/dashboard'), 1500);
            }
          } catch (profileErr) {
            console.warn('[DEBUG] Erreur lors de l\'appel /auth/profile:', profileErr);
            // Fallback : stocker ce qu'on a et rediriger quand même
            localStorage.setItem('access_token', jwtToken);
            localStorage.setItem('token_type', 'Bearer');
            localStorage.setItem('email', formData.email);
            setTimeout(() => navigate('/dashboard'), 1500);
          }
        } else {
          // Pas de token, tentative de login automatique
          try {
            console.log('[DEBUG] Pas de access_token, tentative de login automatique...');
            const loginResp = await fetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/auth/login', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: formData.email,
                password: formData.password
              })
            });
            const loginData = await loginResp.json();
            console.log('[DEBUG] Résultat login automatique:', loginData);
            if (loginResp.ok && loginData.access_token) {
              const loginJwt = loginData.access_token;
              
              // Appeler /auth/profile avec le token du login
              const loginProfileResp = await fetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/auth/profile', {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${loginJwt}`,
                  'Content-Type': 'application/json'
                }
              });
              const loginProfileData = await loginProfileResp.json();
              console.log('[DEBUG] Profile après login:', loginProfileData);
              
              if (loginProfileResp.ok && loginProfileData.id) {
                const userId = loginProfileData.id;
                
                // Tenter l'affiliation
                const refCode = localStorage.getItem('referrer_code');
                if (userId && refCode) {
                  try {
                    const affiliationResult = await logReferralEvent({
                      event: 'signup',
                      userId: userId,
                      price_id: null,
                      timestamp: Date.now()
                    });
                    console.log('✅ Affiliation trackée après login:', affiliationResult);
                  } catch (affiliationError) {
                    console.warn('⚠️ Erreur affiliation après login:', affiliationError);
                  }
                }
                
                localStorage.setItem('access_token', loginJwt);
                localStorage.setItem('token_type', 'Bearer');
                localStorage.setItem('email', formData.email);
                localStorage.setItem('user_id', userId);
              }
              
              setTimeout(() => navigate('/dashboard'), 1500);
            } else {
              setFormData({ email: '', pseudo: '', password: '' });
            }
          } catch (loginErr) {
            console.warn('[DEBUG] Erreur login automatique:', loginErr);
            setFormData({ email: '', pseudo: '', password: '' });
          }
        }
      } else {
        setError(data.detail || 'An error occurred')
      }
    } catch (err) {
      setError(t('auth.error.server'))
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignup = async () => {
    setLoading(true)
    setError('')
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })
      if (error) throw error
    } catch (err) {
      setError(err.message || 'Google sign-up failed')
      setLoading(false)
    }
  }

  const handleGithubSignup = async () => {
    setLoading(true)
    setError('')
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })
      if (error) throw error
    } catch (err) {
      setError(err.message || 'GitHub sign-up failed')
      setLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-black to-slate-950 text-slate-100">
      {/* Bouton retour page d'accueil */}
      <button
        type="button"
        onClick={() => navigate('/')}
        className="absolute top-7 left-7 z-30 flex items-center gap-3 px-6 py-3 rounded-2xl bg-white/10 text-slate-100 text-base font-semibold border border-white/20 shadow-xl backdrop-blur-md hover:bg-white/20 hover:text-emerald-400 transition-all duration-150"
        style={{boxShadow:'0 8px 32px rgba(16,185,129,0.12)', backdropFilter:'blur(12px) saturate(120%)'}}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline-block align-middle"><polyline points="15 18 9 12 15 6"></polyline></svg>
        {t('backHome') || 'Accueil'}
      </button>
      {/* Glows décoratifs */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/25 blur-3xl hidden md:block" />
      <div className="pointer-events-none absolute bottom-[-90px] right-[-90px] h-96 w-96 rounded-full bg-sky-500/20 blur-3xl hidden md:block" />

      <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
        {/* Carte en verre */}
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/5 p-8 shadow-[0_0_1px_rgba(255,255,255,0.25),0_25px_80px_-20px_rgba(16,185,129,0.35)] backdrop-blur-2xl">
          {/* Header */}
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 h-14 w-14 flex items-center justify-center rounded-full bg-white/10 ring-1 ring-inset ring-blue-400/30">
              <img src="/logo_svg.svg" alt="Logo" className="h-12 w-12" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('auth.signup.title')}</h1>
            <p className="mt-1 text-sm text-slate-400">{t('auth.signup.subtitle')}</p>
          </div>

          {/* Messages de statut */}
          {success && (
            <div className="mb-6 rounded-lg bg-emerald-500/20 border border-emerald-500/30 p-3 text-center text-sm text-emerald-300">{t('auth.signup.success')}</div>
          )}
          {error && (
            <div className="mb-6 rounded-lg bg-red-500/20 border border-red-500/30 p-3 text-center text-sm text-red-300">
              {error}
            </div>
          )}

          {/* Boutons OAuth */}
          <div className="flex gap-3">
            {/* Bouton Google */}
            <button
              type="button"
              onClick={handleGoogleSignup}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/5 py-3 font-semibold text-slate-100 transition hover:bg-white/10 hover:border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="hidden sm:inline">Google</span>
            </button>

            {/* Bouton GitHub */}
            <button
              type="button"
              onClick={handleGithubSignup}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/5 py-3 font-semibold text-slate-100 transition hover:bg-white/10 hover:border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v 3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
              <span className="hidden sm:inline">GitHub</span>
            </button>
          </div>

          {/* Séparateur */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-gradient-to-br from-slate-950 via-black to-slate-950 px-2 text-slate-500">ou</span>
            </div>
          </div>

          {/* Formulaire */}
          <form onSubmit={onSubmit} className="space-y-4">
            {/* Email */}
            <div className="space-y-1">
              <label htmlFor="email" className="text-sm text-slate-300">{t('auth.email')}</label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {/* Icône mail */}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5"><path d="M1.5 8.67v8.58A2.25 2.25 0 0 0 3.75 19.5h16.5a2.25 2.25 0 0 0 2.25-2.25V8.67l-8.724 4.362a3.75 3.75 0 0 1-3.252 0L1.5 8.67Z"/><path d="M22.5 6.908V6.75A2.25 2.25 0 0 0 20.25 4.5H3.75A2.25 2.25 0 0 0 1.5 6.75v.158l9.276 4.64a2.25 2.25 0 0 0 1.948 0L22.5 6.908Z"/></svg>
                </span>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={onChange}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-10 py-3 text-slate-100 placeholder:text-slate-500 outline-none ring-0 transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/40"
                  placeholder="email@example.com"
                  required
                />
              </div>
            </div>

            {/* Pseudo */}
            <div className="space-y-1">
              <label htmlFor="pseudo" className="text-sm text-slate-300">{t('auth.pseudo')}</label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {/* Icône user */}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5"><path fillRule="evenodd" d="M12 2.25a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9ZM3.75 20.1a8.25 8.25 0 1 1 16.5 0 .9.9 0 0 1-.9.9h-14.7a.9.9 0 0 1-.9-.9Z" clipRule="evenodd"/></svg>
                </span>
                <input
                  type="text"
                  id="pseudo"
                  name="pseudo"
                  value={formData.pseudo}
                  onChange={onChange}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-10 py-3 text-slate-100 placeholder:text-slate-500 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/40"
                  placeholder="@username"
                  required
                />
              </div>
            </div>

            {/* Mot de passe */}
            <div className="space-y-1">
              <label htmlFor="password" className="text-sm text-slate-300">{t('auth.password')}</label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {/* Icône lock */}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5"><path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25V9A3.75 3.75 0 0 0 3 12.75v4.5A3.75 3.75 0 0 0 6.75 21h10.5A3.75 3.75 0 0 0 21 17.25v-4.5A3.75 3.75 0 0 0 17.25 9V6.75A5.25 5.25 0 0 0 12 1.5Zm-1.125 11.625a1.125 1.125 0 1 1 2.25 0v2.25a1.125 1.125 0 1 1-2.25 0v-2.25Z" clipRule="evenodd"/></svg>
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={onChange}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-10 py-3 text-slate-100 placeholder:text-slate-500 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/40"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300 transition"
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5"><path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"/><path fillRule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.826 0 1.188-1.49 4.467-5.705 7.694-10.675 7.694-4.97 0-9.186-3.223-10.675-7.694a1.126 1.126 0 0 1 0-1.188ZM12 12a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" clipRule="evenodd"/></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5"><path fillRule="evenodd" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" clipRule="evenodd"/></svg>
                  )}
                </button>
              </div>

              {/* Barre de force du mot de passe */}
              {formData.password && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">{t('auth.password.strength')}</span>
                    <span className={`text-xs font-semibold ${
                      passwordStrength < 25 ? 'text-red-400' :
                      passwordStrength < 50 ? 'text-yellow-400' :
                      passwordStrength < 75 ? 'text-blue-400' :
                      'text-emerald-400'
                    }`}>
                      {passwordStrength < 25 ? t('auth.password.veryWeak') :
                       passwordStrength < 50 ? t('auth.password.weak') :
                       passwordStrength < 75 ? t('auth.password.medium') :
                       t('auth.password.strong')}
                    </span>
                  </div>
                  
                  {/* Barre animée */}
                  <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${
                        passwordStrength < 25 ? 'bg-red-500 w-1/4' :
                        passwordStrength < 50 ? 'bg-yellow-500 w-2/4' :
                        passwordStrength < 75 ? 'bg-blue-500 w-3/4' :
                        'bg-emerald-500 w-full'
                      }`}
                      style={{
                        width: `${passwordStrength}%`,
                        boxShadow: passwordStrength === 100 ? `0 0 8px ${
                          passwordStrength < 25 ? 'rgba(239,68,68,0.5)' :
                          passwordStrength < 50 ? 'rgba(234,179,8,0.5)' :
                          passwordStrength < 75 ? 'rgba(59,130,246,0.5)' :
                          'rgba(16,185,129,0.5)'
                        }` : 'none'
                      }}
                    />
                  </div>

                  {/* Vérifications */}
                  <div className="space-y-1 text-xs">
                    <div className={`flex items-center gap-1 ${passwordErrors.includes('min8') ? 'text-red-400' : 'text-emerald-400'}`}>
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        {passwordErrors.includes('min8') ? (
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        )}
                      </svg>
                      {t('auth.password.requirement.min8')}
                    </div>
                    <div className={`flex items-center gap-1 ${passwordErrors.includes('special') ? 'text-red-400' : 'text-emerald-400'}`}>
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        {passwordErrors.includes('special') ? (
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        )}
                      </svg>
                      {t('auth.password.requirement.special')}
                    </div>
                    <div className={`flex items-center gap-1 ${passwordErrors.includes('uppercase') ? 'text-red-400' : 'text-emerald-400'}`}>
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        {passwordErrors.includes('uppercase') ? (
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        )}
                      </svg>
                      {t('auth.password.requirement.uppercase')}
                    </div>
                    <div className={`flex items-center gap-1 ${passwordErrors.includes('number') ? 'text-red-400' : 'text-emerald-400'}`}>
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        {passwordErrors.includes('number') ? (
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        )}
                      </svg>
                      {t('auth.password.requirement.number')}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CTA */}
            <button
              type="submit"
              disabled={loading}
              className="group mt-6 w-full transform rounded-lg bg-emerald-500 py-3 font-semibold text-slate-950 transition hover:-translate-y-0.5 hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/60 focus:ring-offset-0 disabled:cursor-not-allowed disabled:bg-slate-600 disabled:text-slate-300 disabled:transform-none"
            >
              {loading ? t('auth.signup.submitting') : t('auth.signup.submit')}
              {!loading && <span className="ml-1 inline-block transition-transform group-hover:translate-x-0.5">→</span>}
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-400">
            {t('auth.signup.haveAccount')}{' '}
            <Link to="/login" className="text-emerald-400 hover:text-emerald-300">{t('auth.login.link')}</Link>
          </p>
        </div>
      </div>

      {/* Cookie Consent */}
      <GlobalCookieConsent />
    </div>
  )
}
